import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    ArrowsPointingInIcon,
    ArrowsPointingOutIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    EllipsisVerticalIcon,
    FolderIcon,
    PencilSquareIcon,
    PlusCircleIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { Button, PrimaryButton } from "src/components/base/button";
import CreateTagDialog from "src/components/dialogs/create-tag";
import { SimpleTag, SimpleTagDirectory } from "src/api/generated/frontend_internal_v1";
import DeleteTagDialog from "src/components/dialogs/delete-tag";
import { ApiInternal } from "src/api/api_internal";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import WS from "src/api/ws";
import RenameTagDialog from "src/components/dialogs/rename-tag";
import ChangeTagDescDialog from "src/components/dialogs/change-tag-desc";
import MoveTagFromDirectoryDialog from "src/components/dialogs/move-tag-from-directory";
import HeadingLayout from "src/components/base/heading-layout";
import { Subheading } from "src/components/base/heading";
import CreateTagDirectoryDialog from "src/components/dialogs/create-tag-directory";
import UpdateTagDirectoryDialog from "src/components/dialogs/update-tag-directory";
import DeleteTagDirectoryDialog from "src/components/dialogs/delete-tag-directory";
import { UUID } from "src/api/api_common";
import { Text } from "src/components/base/text";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { Divider } from "src/components/base/divider";
import { TagRowAdmin } from "src/components/tag-row-admin";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";

/**
 * The properties for {@link TagManagement}
 */
export type TagManagementProps = {};

/**
 * The management overview for tags
 */
function TagManagement(props: TagManagementProps) {
    const [t] = useTranslation("admin-tag-overview");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { search } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.TagsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    // false -> dialog closed
    // true -> dialog open without directory
    // "<uuid>" -> dialog open with directory
    const [openCreateTag, setOpenCreateTag] = React.useState<UUID | boolean>(false);
    const [openRenameTag, setOpenRenameTag] = React.useState<SimpleTag>();
    const [openSetTagDesc, setOpenSetTagDesc] = React.useState<SimpleTag>();
    const [openDeleteTag, setOpenDeleteTag] = React.useState<SimpleTag>();

    const [openMoveFromDirectory, setOpenMoveFromDirectory] = React.useState<SimpleTag>();

    const [openCreateDirectory, setOpenCreateDirectory] = React.useState(false);
    const [openUpdateDirectory, setOpenUpdateDirectory] = React.useState<SimpleTagDirectory>();
    const [openDeleteDirectory, setOpenDeleteDirectory] = React.useState<SimpleTagDirectory>();

    const [openActiveDirectory, setOpenActiveDirectory] = React.useState<Array<UUID>>([]);

    return (
        <HeadingLayout
            heading={t("heading.tag-overview")}
            headingDescription={t("description.tags")}
            headingChildren={
                <div className={"flex gap-3"}>
                    <Button outline={true} onClick={() => setOpenCreateDirectory(true)}>
                        {t("button.create-tag-directory")}
                    </Button>
                    <PrimaryButton onClick={() => setOpenCreateTag(true)}>{t("button.create-tag")}</PrimaryButton>
                </div>
            }
        >
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/a/tags",
                                        search: {
                                            search: fieldApi.state.value,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 50,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <div className={"flex items-end justify-end gap-3"}>
                    <Button
                        plain={true}
                        title={t("button.expand")}
                        onClick={() => setOpenActiveDirectory(data.directories.map((directory) => directory.uuid))}
                    >
                        <ArrowsPointingOutIcon />
                    </Button>
                    <Button plain={true} title={t("button.collapse")} onClick={() => setOpenActiveDirectory([])}>
                        <ArrowsPointingInIcon />
                    </Button>
                </div>
            </Form>

            <div className={"flex flex-col"}>
                <div className={"grid grid-cols-[1fr_1fr_auto] gap-2"}>
                    <Text>{t("label.tag-name")}</Text>
                    <Text>{t("label.tag-description")}</Text>
                    <div className={"w-10"}>
                        <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                    </div>
                    <Divider className={"col-span-3"} />
                    {data.ungrouped_tags
                        .filter((tag) => tag.name.toLowerCase().includes(form.state.values.search.toLowerCase()))
                        .map((tag) => (
                            <TagRowAdmin
                                key={tag.uuid}
                                tag={tag}
                                setOpenRenameTag={() => setOpenRenameTag(tag)}
                                setOpenSetTagDesc={(tag) => setOpenSetTagDesc(tag)}
                                setOpenMoveFromDirectory={(tag) => setOpenMoveFromDirectory(tag)}
                                setOpenDeleteTag={() => setOpenDeleteTag(tag)}
                                isUngrouped={true}
                            />
                        ))}
                    {data.directories
                        .filter(
                            // Any directory that matches by name or contains a tag that matches will be shown
                            (directory) =>
                                directory.name.toLowerCase().includes(form.state.values.search.toLowerCase()) ||
                                directory.tags.some((tag) =>
                                    tag.name.toLowerCase().includes(form.state.values.search.toLowerCase()),
                                ),
                        )
                        .map((directory) => (
                            <React.Fragment key={directory.uuid}>
                                <Subheading
                                    className={"flex w-full items-center gap-3 hover:cursor-pointer"}
                                    onClick={() =>
                                        openActiveDirectory.includes(directory.uuid)
                                            ? setOpenActiveDirectory(
                                                  openActiveDirectory.filter((dir) => dir !== directory.uuid),
                                              )
                                            : setOpenActiveDirectory([...openActiveDirectory, directory.uuid])
                                    }
                                    key={directory.uuid}
                                >
                                    {openActiveDirectory.includes(directory.uuid) ? (
                                        <ChevronDownIcon className={"size-4"} />
                                    ) : (
                                        <ChevronRightIcon className={"size-4"} />
                                    )}
                                    <FolderIcon className={"size-4"} />
                                    {directory.name}
                                </Subheading>
                                <Text className={"self-center"}>
                                    {t("label.tag-count", { count: directory.tags.length })}
                                </Text>

                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenCreateTag(directory.uuid)}>
                                                <PlusCircleIcon />
                                                <DropdownLabel>{t("button.create-tag")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() =>
                                                    setOpenUpdateDirectory({
                                                        name: directory.name,
                                                        uuid: directory.uuid,
                                                    })
                                                }
                                            >
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.update-tag-directory")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>

                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem
                                                disabled={directory.tags.length !== 0}
                                                onClick={() =>
                                                    setOpenDeleteDirectory({
                                                        name: directory.name,
                                                        uuid: directory.uuid,
                                                    })
                                                }
                                            >
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-tag-directory")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                                <Divider className={"col-span-3"} />
                                {openActiveDirectory.includes(directory.uuid) &&
                                    directory.tags.map((tag) => (
                                        <TagRowAdmin
                                            key={tag.uuid}
                                            tag={tag}
                                            setOpenRenameTag={() => setOpenRenameTag(tag)}
                                            setOpenSetTagDesc={(tag) => setOpenSetTagDesc(tag)}
                                            setOpenMoveFromDirectory={(tag) => setOpenMoveFromDirectory(tag)}
                                            setOpenDeleteTag={() => setOpenDeleteTag(tag)}
                                            isUngrouped={false}
                                        />
                                    ))}
                            </React.Fragment>
                        ))}
                </div>
            </div>

            <Suspense>
                <CreateTagDialog
                    directory_uuid={openCreateTag === true || openCreateTag === false ? undefined : openCreateTag}
                    isOpen={!!openCreateTag}
                    onSubmit={({ directory_uuid }) => {
                        setOpenCreateTag(false);
                        if (directory_uuid === undefined) return;
                        if (openActiveDirectory.find((uuid) => uuid === directory_uuid) === undefined) {
                            setOpenActiveDirectory([...openActiveDirectory, directory_uuid]);
                        }
                    }}
                    onClose={() => setOpenCreateTag(false)}
                />

                <RenameTagDialog
                    tag={openRenameTag}
                    isOpen={!!openRenameTag}
                    onClose={() => setOpenRenameTag(undefined)}
                    onSubmit={() => setOpenRenameTag(undefined)}
                />

                <ChangeTagDescDialog
                    tag={openSetTagDesc}
                    isOpen={!!openSetTagDesc}
                    onClose={() => setOpenSetTagDesc(undefined)}
                    onSubmit={() => setOpenSetTagDesc(undefined)}
                />

                <DeleteTagDialog
                    tag={openDeleteTag}
                    isOpen={!!openDeleteTag}
                    onDeletion={() => setOpenDeleteTag(undefined)}
                    onClose={() => setOpenDeleteTag(undefined)}
                />

                <MoveTagFromDirectoryDialog
                    tag={openMoveFromDirectory}
                    isOpen={!!openMoveFromDirectory}
                    onSubmit={(directory_uuid) => {
                        setOpenMoveFromDirectory(undefined);
                        if (directory_uuid === undefined) return;
                        if (openActiveDirectory.find((uuid) => uuid === directory_uuid) === undefined) {
                            setOpenActiveDirectory([...openActiveDirectory, directory_uuid]);
                        }
                    }}
                    onClose={() => setOpenMoveFromDirectory(undefined)}
                />

                <CreateTagDirectoryDialog isOpen={openCreateDirectory} onClose={() => setOpenCreateDirectory(false)} />

                <UpdateTagDirectoryDialog
                    directory={openUpdateDirectory}
                    isOpen={!!openUpdateDirectory}
                    onClose={() => setOpenUpdateDirectory(undefined)}
                />

                <DeleteTagDirectoryDialog
                    directory={openDeleteDirectory}
                    isOpen={!!openDeleteDirectory}
                    onClose={() => setOpenDeleteDirectory(undefined)}
                />
            </Suspense>
        </HeadingLayout>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type TagsOverviewSearch = {
    /** The search term that should be used */
    search: string;
};

export const Route = createFileRoute("/_admin/a/tags/")({
    component: TagManagement,
    validateSearch: (search: Record<string, unknown>): TagsOverviewSearch => {
        return {
            search: typeof search.search === "string" ? search.search : "",
        };
    },
    loader: async () =>
        await ApiInternal.tags.getAll({
            filter_search: "",
        }),
});
